import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Grid } from "@mui/material";
import { getTecnico, updateTec, getAllEquipos } from "../../../services";
import BreadCrumb from "../../../components/navbar/Breadcrumb";
import provinces from "@doncicuto/es-provinces";
import { ordenarProvinciasYMunicipios } from "../../../functions/ordenarListas";
import { verificarNif, verificarCorreo, verificarTelefono } from "../../../functions/validator/formValidator";

const EditarTecnico = () => {
  const { t } = useTranslation('common');
  const { nif } = useParams();
  const navigate = useNavigate();

  const [tecnico, setTecnico] = useState({
    nif: "",
    email: "",
    nombre: "",
    apellidos: "",
    telefono: "",
    provincia: "",
    team_id: "",
    rol: "",
  });
  const [equipos, setEquipos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    Promise.all([getTecnico(nif), getAllEquipos()])
      .then(([tec, eq]) => {
        setEquipos(eq || []);
        if (!tec) {
          toast.error(t('technicians.messages.notFound', t('messages.error')));
          navigate("/tecnicos");
          return;
        }
        setTecnico({
          nif: tec.nif || "",
          email: tec.user?.email || "",
          nombre: tec.nombre || "",
          apellidos: tec.apellidos || "",
          telefono: tec.telefono || "",
          provincia: tec.provincia || "",
          team_id: tec.team_id ?? "",
          rol: tec.user?.rol?.code || "TECH",
        });
      })
      .catch((err) => {
        console.log(err);
        toast.error(t('messages.error'));
      })
      .finally(() => {
        setCargando(false);
        isFetchingRef.current = false;
      });
  }, [nif]);

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    setTecnico((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const requiredFields = ['nif', 'email', 'nombre', 'apellidos'];
    for (const field of requiredFields) {
      if (!tecnico[field] || String(tecnico[field]).trim() === "") {
        toast.error(t('technicians.messages.fieldRequired', { field }));
        return;
      }
    }
    if (verificarNif(tecnico.nif) === false) {
      toast.error(t('technicians.messages.incorrectNif'));
      return;
    }
    if (verificarCorreo(tecnico.email) === false) {
      toast.error(t('technicians.messages.incorrectEmail'));
      return;
    }
    if (tecnico.telefono && verificarTelefono(tecnico.telefono) === false) {
      toast.error(t('technicians.messages.incorrectPhone'));
      return;
    }

    const toastInfo = toast.info(t('technicians.messages.saving'));
    try {
      const res = await updateTec(nif, tecnico);

      if (res?.status === 201 && res?.data?.message === "TECHNICIAN_UPDATED") {
        toast.update(toastInfo, {
          render: t('technicians.messages.editedSuccess'),
          type: toast.TYPE.SUCCESS,
          autoClose: true,
        });
        navigate("/tecnicos");
        return;
      }

      const status = res?.status || res?.response?.status;
      const message = res?.data?.message || res?.response?.data?.message;

      if (status === 409 && message === "NIF_ALREADY_EXISTS") {
        toast.update(toastInfo, {
          render: t('technicians.messages.nifExists', 'Ese NIF ya está en uso por otro técnico'),
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
        return;
      }
      if (status === 409 && message === "EMAIL_ALREADY_EXISTS") {
        toast.update(toastInfo, {
          render: t('technicians.messages.emailExists', 'Ese email ya está en uso por otro usuario'),
          type: toast.TYPE.ERROR,
          autoClose: true,
        });
        return;
      }

      toast.update(toastInfo, {
        render: t('technicians.messages.editError'),
        type: toast.TYPE.ERROR,
        autoClose: true,
      });
    } catch (err) {
      console.log(err);
      toast.update(toastInfo, {
        render: t('messages.error'),
        type: toast.TYPE.ERROR,
        autoClose: true,
      });
    }
  };

  return (
    <>
      <BreadCrumb
        rutas={[
          { path: '/tecnicos', label: t('technicians.title') },
          { path: `/tecnicos/editar/${nif}`, label: t('actions.edit') }
        ]}
        style={{ margin: '0 10px' }}
      />
      <div className="pageContent">
        <div className="gridHead">
          {t('actions.edit').toUpperCase()} {t('technicians.title').toLowerCase()}
        </div>
        {cargando ? (
          <div style={{ padding: 20 }}>{t('messages.loading', 'Cargando...')}</div>
        ) : (
          <form className="nuevoAAFFForm">
            <Grid container spacing={5} style={{ marginTop: 20, marginBottom: 20, padding: '0 20px' }}>
              <Grid item xs={12} sm={6}>
                <input
                  type="text"
                  required
                  placeholder={t('form.nif')}
                  name="nif"
                  value={tecnico.nif}
                  onChange={handleChange}
                  pattern="(^[0-9]{8}[A-Z]{1})$|^([A-Z]{1}[0-9]{8})$|^([A-Z]{1}[0-9]{7}[A-Z]{1}$)"
                  title={t('form.nif')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  type="email"
                  required
                  placeholder={t('form.email')}
                  name="email"
                  value={tecnico.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  type="text"
                  required
                  placeholder={t('form.firstName')}
                  name="nombre"
                  value={tecnico.nombre}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  type="text"
                  required
                  placeholder={t('form.lastName')}
                  name="apellidos"
                  value={tecnico.apellidos}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  type="tel"
                  placeholder={t('form.phone')}
                  name="telefono"
                  value={tecnico.telefono}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <select
                  name="team_id"
                  value={tecnico.team_id ?? ""}
                  onChange={handleChange}
                >
                  <option value="">{t('technicians.columns.team')}</option>
                  {equipos.map((e) => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
              </Grid>
              <Grid item xs={12} sm={6}>
                <select
                  name="provincia"
                  value={tecnico.provincia}
                  onChange={handleChange}
                >
                  <option value="">{t('form.province')}</option>
                  {provinces.sort(ordenarProvinciasYMunicipios)?.map((e, i) => (
                    <option key={i} value={e.name}>{e.name}</option>
                  ))}
                </select>
              </Grid>
              <Grid item xs={12} sm={6}>
                <select
                  required
                  name="rol"
                  value={tecnico.rol}
                  onChange={handleChange}
                >
                  <option value="TECH">{t('technicians.roles.technician')}</option>
                  <option value="TECH_COOR">{t('technicians.roles.coordinator')}</option>
                </select>
              </Grid>
            </Grid>
            <Grid style={{ display: 'flex', justifyContent: 'end', padding: 20 }}>
              <input
                type="button"
                value={t('actions.save')}
                onClick={handleSubmit}
              />
            </Grid>
          </form>
        )}
      </div>
    </>
  );
};

export default EditarTecnico;
